import type { CommandResult } from '@travel-planner/shared/schemas';
import type { ZodType } from 'zod';

/**
 * Typed accessor for `CommandResult.data`. Renderers receive the result as
 * `{ kind, data: Record<string, unknown>, humanSummary }`, so they normally
 * have to cast `data` to read fields. This helper parses with a Zod schema
 * instead, so a renderer becomes:
 *
 * ```ts
 * const data = parseResultData(result, productListResultData);
 * if (!data) return <Text>{result.humanSummary}</Text>;
 * // data is now strictly typed
 * ```
 *
 * Returns `null` when the schema doesn't match, so the renderer can fall
 * back to `result.humanSummary` instead of crashing on a shape change.
 */
export const parseResultData = <T>(
  result: CommandResult,
  schema: ZodType<T>
): T | null => {
  const parsed = schema.safeParse(result.data);
  return parsed.success ? parsed.data : null;
};
